import { createBrowserRouter } from "react-router-dom";
import { lazy, Suspense } from "react";

// Layout & Wrappers
import Root from "../Layout/Root";
import ErrorOccurPage from "../pages/Error";
import Loader from "../components/Reusable/Loader";
import CookieBanner from "../components/Reusable/CookieBanner";

// Route Guards
import {
  ProtectedClientRoute,
  PublicRoute,
} from "../components/routing/protectedAndPublicRoute";

// Helper wrapper: automatically wraps lazy components in Suspense
const LazyWrapper = (Component) => (
  <Suspense fallback={<Loader text="Loading..." showText={true} size={60} />}>
    <Component />
  </Suspense>
);

// Cookie wrapper
const CookieWrapper = ({ children }) => (
  <>
    <CookieBanner />
    {children}
  </>
);

// Context wrapper
const ContextWrapper = (element) => <CookieWrapper>{element}</CookieWrapper>;

/* -----------------------------
   LAZY IMPORTS (All Pages)
------------------------------ */
const CompanyProfile = lazy(
  () => import("../components/pagesSpecific/Profile/CompanyProfile"),
);
const ReferralOrderDetails = lazy(
  () => import("../pages/ReferreeOrderDetails"),
);
const UserProfile = lazy(
  () => import("../components/pagesSpecific/Profile/UserProfile"),
);
const OnboardingForm = lazy(
  () => import("../components/pagesSpecific/Login/OnboardingForm"),
);
const Thread = lazy(() => import("../components/Chat/Thread"));
const Payment = lazy(() => import("../pages/Payment"));
const MyOrders = lazy(() => import("../pages/MyOrders"));
const ViewOrder = lazy(() => import("../pages/ViewOrder"));
const MyPayments = lazy(() => import("../pages/MyPayments"));
const Invoice = lazy(() => import("../pages/Invoice"));
const Receipt = lazy(() => import("../pages/Receipt"));
const FullChatView = lazy(() => import("../components/Chat/FullChatView"));
const SignInScreen = lazy(
  () => import("../components/pagesSpecific/Login/SignInScreen"),
);
const TermsAndConditions = lazy(
  () => import("../components/pagesSpecific/Login/TermsAndConditions"),
);
const PrivacyPolicy = lazy(
  () => import("../components/pagesSpecific/Login/PrivacyPolicy"),
);
const PaymentSuccessfulPage = lazy(
  () => import("../pages/PaymentSuccessfulPage"),
);
const PaymentFailedPage = lazy(() => import("../pages/PaymentFailedPage"));
const SubscriptionPaymentSuccessfulPage = lazy(
  () => import("../pages/SubscriptionPaymentSuccessfulPage"),
);
const SubscriptionPaymentFailedPage = lazy(
  () => import("../pages/SubscriptionPaymentFailedPage"),
);
const TestReusableComponents = lazy(
  () => import("../pages/TestReusableComponents"),
);
const InternalServerErrorPage = lazy(
  () => import("../pages/InternalServerError"),
);
const PendingApproval = lazy(
  () => import("../components/pagesSpecific/Login/PendingApproval"),
);
const NotFoundPage = lazy(() => import("../pages/NotFoundError"));
// const Services = lazy(() => import("../pages/Services"));
const CreateOffer = lazy(() => import("../pages/CreateOffer"));
const OfferMessageComponent = lazy(
  () => import("../components/Chat/OfferMessageComponent"),
);
const SubscriptionPlans = lazy(() => import("../pages/SubscriptionPlans"));
const SubscriptionCheckout = lazy(
  () => import("../pages/SubscriptionCheckout"),
);
const MySubscriptions = lazy(() => import("../pages/MySubscriptions"));
const ManageSubscription = lazy(() => import("../pages/ManageSubscription"));
const AiWorkFlowPlanManagement = lazy(
  () => import("../pages/AiWorkFlowPlanManagement"),
);
const OrderChat = lazy(() => import("../components/Chat/OrderChat"));
const MySubscriptionPayments = lazy(
  () => import("../pages/MySubscriptionPayments"),
);
const SubscriptionReceiptPage = lazy(
  () => import("../pages/SubscriptionReceiptPage"),
);
const AboutUs = lazy(() => import("../pages/AboutUs"));
const RefundPolicy = lazy(() => import("../pages/RefundPolicy"));

/* -----------------------------
          ROUTES
------------------------------ */

const router = createBrowserRouter([
  {
    path: "/",
    element: ContextWrapper(<Root />),
    errorElement: <ErrorOccurPage />,

    children: [
      // ---------- PUBLIC ROUTES ----------
      {
        index: true,
        element: LazyWrapper(OnboardingForm),
        errorElement: <ErrorOccurPage />,
      },
      {
        path: "onboarding",
        element: LazyWrapper(OnboardingForm),
      },
      {
        path: "login",
        element: LazyWrapper(SignInScreen),
      },
      {
        path: "reset-password",
        element: LazyWrapper(OnboardingForm),
      },
      {
        path: "signin",
        element: LazyWrapper(OnboardingForm),
      },
      {
        path: "pending-approval",
        element: LazyWrapper(PendingApproval),
      },
      {
        path: "",
        element: <PublicRoute />,
        children: [],
      },

      // ---------- PROTECTED CLIENT ROUTES ----------
      {
        element: <ProtectedClientRoute />,
        children: [
          {
            path: "create-offer/:offerId?",
            element: LazyWrapper(CreateOffer),
          },
          {
            path: "offer-msg-component/:offerId?",
            element: LazyWrapper(OfferMessageComponent),
          },
          { path: "company-profile", element: LazyWrapper(CompanyProfile) },
          { path: "user-profile", element: LazyWrapper(UserProfile) },
          {
            path: "/referral-order/:orderId",
            element: LazyWrapper(ReferralOrderDetails),
          },
          {
            path: "payment-page/:offerId?",
            element: LazyWrapper(Payment),
          },
          { path: "orders", element: LazyWrapper(MyOrders) },
          {
            path: "orders/:orderId",
            element: LazyWrapper(ViewOrder),
          },
          {
            path: "orders/:orderId/chat",
            element: LazyWrapper(OrderChat),
          },
          { path: "my-payments", element: LazyWrapper(MyPayments) },
          {
            path: "services",
            element: LazyWrapper(SubscriptionPlans),
          },
          {
            path: "subscription-checkout",
            element: LazyWrapper(SubscriptionCheckout),
          },
          {
            path: "subscription-capacity-checkout",
            element: LazyWrapper(SubscriptionCheckout),
          },
          {
            path: "my-subscriptions",
            element: LazyWrapper(MySubscriptions),
          },
          {
            path: "my-subscription-payments",
            element: LazyWrapper(MySubscriptionPayments),
          },
          {
            path: "manage-subscriptions/:subscriptionId",
            element: LazyWrapper(ManageSubscription),
          },
          {
            path: "ai-workflow-management/:subscriptionId",
            element: LazyWrapper(AiWorkFlowPlanManagement),
          },
          {
            path: "payment-successful",
            element: LazyWrapper(PaymentSuccessfulPage),
          },
          {
            path: "payment-failed",
            element: LazyWrapper(PaymentFailedPage),
          },
          {
            path: "subscription-payment-successful",
            element: LazyWrapper(SubscriptionPaymentSuccessfulPage),
          },
          {
            path: "subscription-payment-failed",
            element: LazyWrapper(SubscriptionPaymentFailedPage),
          },
          {
            path: "full-chat-view",
            element: LazyWrapper(FullChatView),
          },
          {
            path: "full-chat-view/:threadId?",
            element: LazyWrapper(FullChatView),
          },
        ],
      },
    ],
  },

  // ---------- OUTSIDE ROOT ----------
  {
    path: "invoice/:invoiceId?",
    element: ContextWrapper(LazyWrapper(Invoice)),
  },
  {
    path: "receipt/:receiptId?",
    element: ContextWrapper(LazyWrapper(Receipt)),
  },
  {
    path: "/subscription-receipt/:paymentId",
    element: LazyWrapper(SubscriptionReceiptPage),
  },
  {
    path: "terms-and-conditions",
    element: ContextWrapper(LazyWrapper(TermsAndConditions)),
  },
  {
    path: "privacy-policy",
    element: ContextWrapper(LazyWrapper(PrivacyPolicy)),
  },
  {
    path: "about-us",
    element: ContextWrapper(LazyWrapper(AboutUs)),
  },
  {
    path: "refund-policy",
    element: ContextWrapper(LazyWrapper(RefundPolicy)),
  },

  // Testing & Errors
  { path: "reusable", element: LazyWrapper(TestReusableComponents) },
  {
    path: "/server-error",
    element: ContextWrapper(LazyWrapper(InternalServerErrorPage)),
  },
  {
    path: "/error",
    element: ContextWrapper(<ErrorOccurPage />),
  },

  // Catch-all
  {
    path: "*",
    element: ContextWrapper(LazyWrapper(NotFoundPage)),
  },
]);

export default router;
